// Performance Type Color Settings Component

import React, { useState, useEffect } from 'react';
import { PerformanceTypeColorSettingsProps } from '../../types/colorTypes';
import { usePerformanceTypeColors } from '../../hooks/usePerformanceTypeColors';
import { getPerformanceTypeColor } from '../../utils/performanceTypeColors';
import { ColorPicker } from './ColorPicker';
import { validateColorAccessibility } from '../../utils/colorUtilities';
import { useConfirmModal } from '../common/ConfirmModal';
import './PerformanceTypeColorSettings.css';

export const PerformanceTypeColorSettings: React.FC<PerformanceTypeColorSettingsProps> = ({
  availablePerformanceTypes,
  onColorsChange
}) => {
  const { confirm, ConfirmModal } = useConfirmModal();
  const {
    customColors,
    setCustomColorByBackground,
    resetToDefaults,
    resetPerformanceTypeToDefault,
    hasCustomColor,
    validateColorScheme,
    downloadColorScheme,
    importColorSchemeFromJSON
  } = usePerformanceTypeColors();

  const [importValue, setImportValue] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [validationResults, setValidationResults] = useState<{[key: string]: any}>({});

  // Notify parent component when colors change
  useEffect(() => {
    if (onColorsChange) {
      onColorsChange(customColors);
    }
  }, [customColors, onColorsChange]);

  // Validate colors when they change
  useEffect(() => {
    const results: {[key: string]: any} = {};
    availablePerformanceTypes.forEach(type => {
      const color = getPerformanceTypeColor(type);
      const validation = validateColorAccessibility(
        color.backgroundColor,
        color.textColor,
        color.borderColor
      );
      results[type] = validation;
    });
    setValidationResults(results);
  }, [availablePerformanceTypes, customColors]);

  const handleColorChange = (performanceType: string, backgroundColor: string) => {
    setCustomColorByBackground(performanceType, backgroundColor);
  };

  const handleResetAll = () => {
    confirm({
      title: 'Reset Colors',
      message: 'Are you sure you want to reset all colors to defaults? This cannot be undone.',
      confirmText: 'Reset All',
      variant: 'warning',
      onConfirm: () => {
        resetToDefaults();
      }
    });
  };

  const handleResetSingle = (performanceType: string) => {
    resetPerformanceTypeToDefault(performanceType);
  };

  const handleExport = () => {
    const schemeName = prompt('Enter a name for your color scheme:');
    if (schemeName) {
      const description = prompt('Enter a description (optional):');
      downloadColorScheme(schemeName, description || undefined);
    }
  };

  const handleImport = () => {
    try {
      const success = importColorSchemeFromJSON(importValue);
      if (success) {
        setImportValue('');
        setShowImport(false);
        alert('Color scheme imported successfully!');
      } else {
        alert('Failed to import color scheme. Please check the format.');
      }
    } catch (error) {
      alert('Invalid JSON format. Please check your color scheme data.');
    }
  };

  const handleFileImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        if (content) {
          setImportValue(content);
        }
      };
      reader.readAsText(file);
    }
  };

  const overallValidation = validateColorScheme(customColors);

  return (
    <div className="performance-type-color-settings">
      <div className="settings-header">
        <h3>Performance Type Colors</h3>
        <p className="text-muted">
          Customize colors for each performance type to make them easily distinguishable in your planning view.
        </p>
      </div>

      {/* Validation Summary */}
      {(overallValidation.errors.length > 0 || overallValidation.warnings.length > 0) && (
        <div className="validation-summary">
          {overallValidation.errors.length > 0 && (
            <div className="alert alert-danger">
              <strong>Accessibility Issues:</strong>
              <ul className="mb-0 mt-1">
                {overallValidation.errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}
          {overallValidation.warnings.length > 0 && (
            <div className="alert alert-warning">
              <strong>Accessibility Warnings:</strong>
              <ul className="mb-0 mt-1">
                {overallValidation.warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Color Settings Grid */}
      <div className="color-settings-grid">
        {availablePerformanceTypes.map((performanceType) => {
          const currentColor = getPerformanceTypeColor(performanceType);
          const isCustom = hasCustomColor(performanceType);
          const validation = validationResults[performanceType];

          return (
            <div key={performanceType} className="color-setting-item">
              <div className="color-setting-header">
                <h5>{performanceType}</h5>
                {isCustom && (
                  <button
                    className="btn btn-sm btn-outline-secondary reset-btn"
                    onClick={() => handleResetSingle(performanceType)}
                    title="Reset to default"
                  >
                    Reset
                  </button>
                )}
              </div>

              <div className="color-setting-content">
                <ColorPicker
                  label="Background Color"
                  color={currentColor.backgroundColor}
                  onChange={(color) => handleColorChange(performanceType, color)}
                />

                <div className="color-preview-container">
                  <div className="color-preview-label">Preview:</div>
                  <div
                    className="color-preview-sample"
                    style={{
                      backgroundColor: currentColor.backgroundColor,
                      borderColor: currentColor.borderColor,
                      color: currentColor.textColor
                    }}
                  >
                    {performanceType}
                  </div>
                </div>

                {validation && !validation.isValid && (
                  <div className="color-validation-errors">
                    <small className="text-danger">
                      {validation.errors.join(', ')}
                    </small>
                  </div>
                )}

                {validation && validation.warnings.length > 0 && (
                  <div className="color-validation-warnings">
                    <small className="text-warning">
                      {validation.warnings.join(', ')}
                    </small>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Action Buttons */}
      <div className="color-settings-actions">
        <div className="action-group">
          <button
            className="btn btn-outline-danger"
            onClick={handleResetAll}
            disabled={Object.keys(customColors).length === 0}
          >
            Reset All to Defaults
          </button>
        </div>

        <div className="action-group">
          <button
            className="btn btn-outline-primary"
            onClick={handleExport}
            disabled={Object.keys(customColors).length === 0}
          >
            Export Color Scheme
          </button>
          
          <button
            className="btn btn-outline-secondary"
            onClick={() => setShowImport(!showImport)}
          >
            Import Color Scheme
          </button>
        </div>
      </div>

      {/* Import Section */}
      {showImport && (
        <div className="import-section">
          <h4>Import Color Scheme</h4>
          
          <div className="import-methods">
            <div className="import-method">
              <label htmlFor="file-import" className="form-label">
                Import from file:
              </label>
              <input
                id="file-import"
                type="file"
                accept=".json"
                onChange={handleFileImport}
                className="form-control"
              />
            </div>

            <div className="import-divider">
              <span>OR</span>
            </div>

            <div className="import-method">
              <label htmlFor="json-import" className="form-label">
                Paste JSON data:
              </label>
              <textarea
                id="json-import"
                className="form-control"
                rows={6}
                value={importValue}
                onChange={(e) => setImportValue(e.target.value)}
                placeholder="Paste your color scheme JSON here..."
              />
            </div>
          </div>

          <div className="import-actions">
            <button
              className="btn btn-primary"
              onClick={handleImport}
              disabled={!importValue.trim()}
            >
              Import
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowImport(false);
                setImportValue('');
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      <ConfirmModal />
    </div>
  );
};